import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Pagination,
  Table,
  Input,
  InputGroup,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { CommercialServices } from "../../../core/services/CommercialServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { AlertModal } from "../../components/Modals/AlertModal/AlertModal";
import { CommercialFields, CommercialListParams } from "./CommercialDataTypes";
import { Permission } from "../../../core/types/PermissionInterface";
import "./Commercials.css";
import CommercialFilters from "./CommercialFilters";
import {
  isEmptyVal,
  isNotEmpty,
  displayField,
  getLabelFor,
} from "../../../utils/helpers";
import ImageCellFormatter from "../../components/Elements/ImageCellFormatter";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import CheckCell from "../../components/Elements/CheckCell";
import CommercialSingleAction from "./CommercialSingleAction";
import CommercialBulkAction from "./CommercialBulkAction";
import { ContentRoutes } from "../../../navigation/NavConstant";
import { ToastContainer } from "react-toastify";
import ImageModal from "../../components/Modals/ImageModal/ImageModal";
import {
  statusArr,
  messageTypeArr,
  appConstObj,
  roleTypeArr,
  mediaNatureObjArr,
  commercialUseForObjArr,
  mediaTypeObjArr,
  mediaSourceObjArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash";
import MediaPlay from "../../components/Elements/MediaPlay";
import MediaPlayerFormatter from "../../components/Elements/MediaPlayerFormatter";
import { useSelector } from "react-redux";
import { useMediaContext } from "../../../context/MediaContext";
import AudioPlayer from "../../components/Elements/AudioPlayer";

interface ImageModal {
  openModal: () => void;
  removePic: () => void;
  hideDelete: () => void;
}

function Commercials() {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const [loading, setLoading] = useState(false);
  const company = useSelector((state: any) => state.company);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<CommercialFields[]>([]);
  const [sort_by, setSortBy] = useState<any>();
  const [sort_order, setSortOrder] = useState<any>();
  const modalRef = useRef<ImageModal>(null);
  const [selectedCommercial, setSelectedCommercialData] = useState<any>({});
  const [openPictureDelModal, setOpenPictureDelModal] = useState(false);
  const [smartSearchInput, setSmartSearch] = useState<string>("");
  const loggedInUserDetail = UtilityServices.getLoggedInUserDetails();
  const initSearchFormData = {
    name: "",
    media_type: "",
    sourced_from: "",
    media_nature: "",
    commercial_use_for: "",
    company: "",
    brand: "",
    campaign: "",
    status: "",
    smart_search: "",
  };
  const { playingMediaInfo, setPlayingMediaInfo, isPlaying, setIsPlaying } =
    useMediaContext();
  const [formData, setFormData] =
    useState<CommercialListParams>(initSearchFormData);
  const [showCreate, setShowCreate] = useState<boolean>();
  const navigate = useNavigate();
  const [checkedKeys, setCheckedKeys] = useState<any[]>([]);
  let checked = false;
  let indeterminate = false;
  const [failedImages, setFailedImages] = useState<any>([]);

  if (checkedKeys?.length === data?.length) {
    checked = true;
  } else if (checkedKeys?.length === 0) {
    checked = false;
  } else if (checkedKeys?.length > 0 && checkedKeys?.length < data?.length) {
    indeterminate = true;
  }

  const handleCheckAll = (value: any, checked: any) => {
    const keys = checked ? data.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };

  const handleCheck = (value: number, checked: any) => {
    const keys = checked
      ? [...checkedKeys, value]
      : checkedKeys.filter((item) => item !== value);
    setCheckedKeys(keys);
  };

  async function getCommercials(params: CommercialListParams) {
    try {
      setLoading(true);
      const res = await CommercialServices.getCommercials(params);
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      setCheckedKeys([]);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  function init() {
    setShowCreate(UtilityServices.hasPermission("create-commercial"));
  }

  useEffect(() => {
    if (getCompanyId() !== null) {
      getCommercials(
        getSearchParams({
          company: getCompanyId(),
          page: page,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
    }
    init();
  }, []);

  useEffect(() => {
    if (company.id) {
      getCommercials(
        getSearchParams({
          company: company.id,
          page: page,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
    }
    setPage(1);
  }, [company]);

  function getCompanyId() {
    let compId = null;
    if (UtilityServices.getLoggedInUser("user_type") === roleTypeArr.client) {
      compId = UtilityServices.getLoggedInUser("company_id");
    } else if (company?.id) {
      compId = company.id;
    }
    return compId;
  }

  async function deleteCommercial(commercial: any) {
    try {
      let res = await CommercialServices.deleteCommercial(
        commercial?.current?.id
      );
      setPage(1);
      getCommercials(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function publishCommercial(commercial: any) {
    try {
      let status =
        commercial?.current?.status == statusArr.inactive
          ? statusArr.active
          : statusArr.inactive;

      let res = await CommercialServices.publishCommercial(
        commercial?.current?.id,
        {
          status,
        }
      );
      setPage(1);
      getCommercials(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function bulkPublishAction(status: string) {
    try {
      let res = await CommercialServices.publishBulkCommercial(checkedKeys, {
        status,
      });
      setPage(1);
      getCommercials(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function bulkDeleteAction() {
    try {
      let res = await CommercialServices.deleteBulkCommercial(checkedKeys);
      setPage(1);
      getCommercials(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function exportCommercialAction() {
    try {
      await CommercialServices.exportCommercials(getSearchParams());
    } catch (error) {
      console.log(error);
    }
  }

  async function deletePic() {
    try {
      setOpenPictureDelModal(false);

      let res = await CommercialServices.deleteCommercialLogo(
        selectedCommercial.id
      );
      setPage(1);
      getCommercials(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  function getFailedImages(val: any) {
    setFailedImages(val);
  }

  function commercialPicModal(value: any) {
    if (
      !failedImages.includes(value.media_image) &&
      value.media_image != null &&
      value.media_image !== ""
    ) {
      setSelectedCommercialData(value);
      modalRef.current?.openModal();
    }
  }

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ smart_search: smartSearchInput },
      ...{ company: getCompanyId() },
      ...{ sort_by, sort_order },
      ...{ rows },
      ...data,
    };
    let params: CommercialListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }
    return params;
  }

  function onPageChange(page: number) {
    setPage(page);
    getCommercials(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getCommercials(
      getSearchParams({
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      })
    );
  }

  function submitSearch(formData: any) {
    setFormData(formData);
    getCommercials(getSearchParams(formData));
  }

  function clearSearch() {
    setSmartSearch("");
    setFormData(initSearchFormData);
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getCommercials(
      getSearchParams({
        ...initSearchFormData,
        ...{
          page: 1,
          rows: appConstObj.record_per_page,
        },
      })
    );
  }

  const filterCommercials = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getCommercials(
        getSearchParams({ smart_search: smartSearchInput, rows: rows })
      );
    }
  };

  function processCreateCommercial() {
    if (
      UtilityServices.getLoggedInUser("user_type") == roleTypeArr.backend &&
      !company.id
    ) {
      UtilityServices.showToaster({
        message: t("REQUIRED_SELECT", {
          field: t("COMPANY").toLowerCase(),
        }),
        type: messageTypeArr.error,
      });
      return;
    }
    navigate(ContentRoutes.CREATE_COMMERCIAL);
  }

  const clearSmartSearch = () => {
    setSmartSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getCommercials(
      getSearchParams({
        smart_search: "",
        page: 1,
        sort_by: null,
        sort_order: null,
        rows: appConstObj.record_per_page,
      })
    );
  };

  const handleChangeRows = (rows: any) => {
    setRows(rows);
    setPage(1);
    getCommercials(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };

  const publishPermission = UtilityServices.hasPermission("publish-commercial");
  const updatePermission = UtilityServices.hasPermission("update-commercial");
  const deletePermission = UtilityServices.hasPermission("delete-commercial");
  const createUserPermission = UtilityServices.hasPermission("create-user");
  const permissionObj: Permission = {
    publish: publishPermission,
    update: updatePermission,
    delete: deletePermission,
    create_user: createUserPermission,
  };

  return (
    <div className="commercials">
      <Container className="container">
        <Header className="header">
          <FlexboxGrid justify="space-between">
            <FlexboxGrid.Item colspan={8}>
              <h4>{t("COMMERCIALS")}</h4>
            </FlexboxGrid.Item>
            {showCreate && (
              <FlexboxGrid.Item colspan={4} style={{ textAlign: "end" }}>
                <Button appearance="primary" onClick={processCreateCommercial}>
                  {t("CREATE")}
                </Button>
              </FlexboxGrid.Item>
            )}
          </FlexboxGrid>
        </Header>
        <Content className="commercials">
          {UtilityServices.hasLoggedInUserType(roleTypeArr.backend) && (
            <>
              <CommercialFilters
                onSubmit={submitSearch}
                onClear={clearSearch}
              />
              <div className="row commercial-controls-row">
                <CommercialBulkAction
                  checkedKeys={checkedKeys}
                  setCheckedKeys={setCheckedKeys}
                  onBulkPublish={bulkPublishAction}
                  onBulkDelete={bulkDeleteAction}
                  onExportCommercial={exportCommercialAction}
                />
                <div className="col-md-4">
                  <div className="commercial-smart-search-wrap">
                    <InputGroup className="commercial-smart-search">
                    <Input
                      className="form-control"
                      placeholder={t("SMART_SEARCH_BY", {
                        entity:
                          t("COMMERCIAL").toLowerCase() +
                          " " +
                          t("NAME").toLowerCase() +
                          ", " +
                          t("SINGER").toLowerCase() +
                          ", " +
                          t("OR").toLowerCase() +
                          " " +
                          t("NAME").toLowerCase(),
                      })}
                      value={smartSearchInput}
                      onChange={(val: any) => setSmartSearch(val)}
                      onKeyDown={(e: any) => filterCommercials(e)}
                    />
                    <InputGroup.Button
                      title={t("SEARCH")}
                      onClick={(e: any) => filterCommercials(e)}
                    >
                      <SearchIcon />
                    </InputGroup.Button>
                    <InputGroup.Button
                      title={t("CLEAR")}
                      onClick={() => clearSmartSearch()}
                    >
                      <CloseIcon />
                    </InputGroup.Button>
                    </InputGroup>
                  </div>
                </div>
              </div>
            </>
          )}

          <div
            className={total > 0 ? "commercial-table" : "commercial-empty-wrap"}
          >
            <Table
              height={400}
              fillHeight={true}
              data={data}
              loading={loading}
              onSortColumn={onSortChange}
              sortColumn={sort_by}
              bordered={true}
              cellBordered
              sortType={sort_order}
              hover={true}
              renderEmpty={() => (
                <div className="commercial-empty-state">
                  {getCompanyId() !== null ? (
                    <>
                      <div className="commercial-empty-icon">0</div>
                      <h4 className="commercial-empty-title">
                        {t("NO_ENTITY_FOUND", { entity: t("COMMERCIALS") })}
                      </h4>
                      <p className="commercial-empty-text">
                        {t("ADJUST_FILTER")}
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="commercial-empty-icon">?</div>
                      <h4 className="commercial-empty-title">
                        {t("PLEASE_SELECT_FIELD", { field: t("COMPANY") })}
                      </h4>
                      <p className="commercial-empty-text">
                        Choose a company to load matching commercials.
                      </p>
                    </>
                  )}
                </div>
              )}
            >
              <Column width={40} align="center" fixed>
                <CustomHeaderCell>
                  <div>
                    <Checkbox
                      inline
                      checked={checked}
                      indeterminate={indeterminate}
                      onChange={handleCheckAll}
                    />
                  </div>
                </CustomHeaderCell>
                <CheckCell
                  dataKey="id"
                  checkedKeys={checkedKeys}
                  onChange={handleCheck}
                />
              </Column>
              <Column width={60} align="center" fixed>
                <CustomHeaderCell>{t("PLAY")}</CustomHeaderCell>
                <MediaPlayerFormatter
                  rowData={(rowData: any) => rowData}
                  mediaUrlKey={"media_url"}
                  mediaImageKey={"media_image"}
                />
              </Column>
              <Column width={200} sortable resizable fixed>
                <CustomHeaderCell>{t("NAME")}</CustomHeaderCell>
                <CustomCell dataKey="title">
                  {(rowData: any) => (
                    <span title={`${rowData.title}`}>
                      {displayField(rowData.title)}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={160} flexGrow={1} sortable resizable>
                <CustomHeaderCell>{t("BRAND")}</CustomHeaderCell>
                <CustomCell dataKey="brand_name">
                  {(rowData: any) => (
                    <span>{displayField(rowData.brand_name)}</span>
                  )}
                </CustomCell>
              </Column>
              <Column width={160} sortable resizable>
                <CustomHeaderCell>{t("CAMPAIGN")}</CustomHeaderCell>
                <CustomCell dataKey="campaign_name">
                  {(rowData: any) => (
                    <span title={`${rowData.campaign_name}`}>
                      {displayField(rowData.campaign_name)}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={100} sortable>
                <CustomHeaderCell>{t("TYPE")}</CustomHeaderCell>
                <CustomCell dataKey="media_nature">
                  {(rowData: any) => (
                    <span title={`${rowData.media_nature}`}>
                      {getLabelFor(rowData.media_nature, mediaNatureObjArr)}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={100} sortable>
                <CustomHeaderCell>
                  {t("USE")} {t("FOR")}
                </CustomHeaderCell>
                <CustomCell dataKey="commercial_use_for">
                  {(rowData: any) => (
                    <span title={`${rowData.commercial_use_for}`}>
                      {getLabelFor(
                        rowData.commercial_use_for,
                        commercialUseForObjArr
                      )}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={100} sortable>
                <CustomHeaderCell>{t("FORMAT")}</CustomHeaderCell>
                <CustomCell dataKey="media_type">
                  {(rowData: any) => (
                    <span title={`${rowData.media_type}`}>
                      {getLabelFor(rowData.media_type, mediaTypeObjArr)}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={120} sortable resizable>
                <CustomHeaderCell>
                  {t("SOURCED")} {t("FROM")}
                </CustomHeaderCell>
                <CustomCell dataKey="sourced_from">
                  {(rowData: any) => (
                    <span>
                      {getLabelFor(rowData.sourced_from, mediaSourceObjArr)}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={150} fixed="right">
                <CustomHeaderCell>{t("ACTION")}</CustomHeaderCell>
                <Cell>
                  {(rowData) => (
                    <CommercialSingleAction
                      commercialData={rowData}
                      onDeleteCommercial={deleteCommercial}
                      onPublishCommercial={publishCommercial}
                      permission={permissionObj}
                    />
                  )}
                </Cell>
              </Column>
            </Table>
          </div>
        </Content>
        <Footer className="footer">
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["total", "-", "limit", "|", "pager", "skip"]}
            total={total}
            limitOptions={appConstObj.limit_options}
            limit={rows}
            activePage={page}
            onChangePage={onPageChange}
            onChangeLimit={handleChangeRows}
          />
        </Footer>
        <AudioPlayer />
        <ToastContainer />
      </Container>
      <ImageModal
        ref={modalRef}
        modalTitle={t("LOGO")}
        heading={t("LOGO")}
        body={
          <img
            src={selectedCommercial.media_image}
            className="main-profile-pic"
            alt={t("LOGO")}
          />
        }
        removePic={() => setOpenPictureDelModal(true)}
      />
      <AlertModal
        open={openPictureDelModal}
        handleClose={(e: any) => {
          if (e == true) {
            deletePic();
          } else {
            setOpenPictureDelModal(false);
            setSelectedCommercialData({});
          }
        }}
        displayText={t("CONFIRM_ACTION", {
          action: t("DELETE").toLowerCase(),
          entity: t("COMMERCIAL").toLowerCase() + " " + t("LOGO").toLowerCase(),
        })}
        modalTitle={t("CONFIRM_ACTION_TITLE", {
          action: t("DELETE").toLowerCase(),
        })}
      />
    </div>
  );
}

export default Commercials;
